import React, { useCallback, useMemo, useState } from "react";
import { mexicanStates, usStates } from "../../Helper/CountryStates";
import { useTranslation } from "react-i18next";
import MaintenanceReportLite from "../MaintenanceReports/Modals/MaintenanceReportLite";
import TrailerSearchSelect from "../../Components/Inputs/TrailerSearch";
import TruckSearchSelect from "../../Components/Inputs/TruckSearch";
import { FormInput, FormSelect } from "../../Components/Inputs/FormInputs";
import {
  CARRIER_OPTIONS,
  COUNTRY_OPTIONS,
  GUARD_VEHICLE_CONDITIONS,
} from "../../Constants/CheckInOut";
import DriverSearch from "../../Components/Inputs/DriverSearch";
import SealInventoryInput from "../../Components/Inputs/SealInventoryInput";

function GetMaintenanceCondition(repair) {
  if (!repair) return null;
  const yard_config = repair.location;
  if (yard_config && yard_config.is_active) return "under-repair";
  return "external-repair";
}

const CheckInVehicleForm = ({
  formState,
  formData,
  setFormData,
  dispatchForm,
  close,
  nextForm,
}) => {
  const { t } = useTranslation();
  const [maintenanceReport, setMaintenanceReport] = useState(null);

  const [customerQuery, setCustomerQuery] = useState("");

  const resetTrailerData = () => {
    setFormData((prev) => ({
      ...prev,
      trailerNumber: null,
      trailerPlate: null,
    }));
  };

  const handleFormField = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value.toUpperCase(),
    }));
  };

  const handleConditionChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Updates only states for the selected truck in Select component
  const handleTruckSelect = useCallback(
    (selectedTruck) => {
      if (!selectedTruck) {
        setFormData((prev) => ({
          ...prev,
          truckAsset: null,
          truckPlate: "",
          truckRepairs: null,
          truckCondition: null,
          truckOption: null,
          truckNumber: null,
        }));
        return;
      }

      const hasRepairsOpen = selectedTruck.truckRepairs?.length > 0;

      setFormData((prev) => ({
        ...prev,
        truckAsset: selectedTruck.value,
        truckPlate: selectedTruck.licensePlate,
        truckRepairs: selectedTruck.truckRepairs,
        // Update truck condition based on repairs and lock the field
        truckCondition: hasRepairsOpen ? GetMaintenanceCondition(selectedTruck.truckRepairs[0]) : prev.truckCondition,
        truckOption: selectedTruck,
        truckNumber: `${(prev.carrierName || "").toUpperCase()} ${
          selectedTruck.label
        }`,
      }));
      if (hasRepairsOpen)
        dispatchForm({
          type: "ERROR_MESSAGE",
          key: "truckErrorMessage",
          value: null,
        });
    },
    [dispatchForm, setFormData],
  );

  // Updates only states for the selected trailer in Select component
  const handleTrailerSelect = useCallback(
    (selectedTrailer) => {
      if (!selectedTrailer) {
        setFormData((prev) => ({
          ...prev,
          trailerNumber: "",
          trailerPlate: "",
          trailerRepairs: null,
          trailerCondition: null,
          trailerOption: null,
          trailerAsset: null,
        }));
        return;
      }

      const hasRepairsOpen = selectedTrailer.trailerRepairs?.length > 0;
      setFormData((prev) => ({
        ...prev,
        trailerNumber: selectedTrailer.label,
        trailerPlate: selectedTrailer.licensePlate,
        trailerRepairs: selectedTrailer.trailerRepairs,
        trailerCondition: hasRepairsOpen
          ? GetMaintenanceCondition(selectedTrailer.trailerRepairs[0])
          : prev.trailerCondition,
        trailerOption: selectedTrailer,
        trailerAsset: selectedTrailer.value,
      }));

      if (hasRepairsOpen)
        dispatchForm({
          type: "ERROR_MESSAGE",
          key: "trailerErrorMessage",
          payload: null,
        });
    },
    [dispatchForm, setFormData],
  );

  const handleTrailerDimensions = (e) => {
    const id = e.target.id;
    var length,
      width,
      height,
      standard = 0;
    if (id === "dimension1" && e.target.checked) {
      length = 53;
      width = 8.5;
      height = 13.5;
      standard = 53;
    }
    if (id === "dimension2" && e.target.checked) {
      length = 48;
      width = 8.5;
      height = 13.5;
      standard = 48;
    }
    if (id === "dimension3" && e.target.checked) {
      length = 40;
      width = 8.5;
      height = 13.5;
      standard = 40;
    }
    if (id === "dimension4" && e.target.checked) {
      length = 0;
      width = 0;
      height = 0;
    }
    setFormData((prev) => ({
      ...prev,
      trailerLength: length,
      trailerWidth: width,
      trailerHeight: height,
      trailerDimension: standard,
    }));
  };

  const handleInputChange = useCallback(
    (e) => {
      handleFormField(e);
      setCustomerQuery(e.target.value);
    },
    [handleFormField],
  );

  const handleCustomerClick = (name) => {
    setCustomerQuery("");
    setFormData((prev) => ({
      ...prev,
      customer: name,
    }));
  };

  // Store this form's data in localStorage to restore incase of progress loss
  const storeData = () => {
    const formDataString = JSON.stringify(formData);
    localStorage.setItem("checkInFormData", formDataString);

    const formStateString = JSON.stringify(formState);
    localStorage.setItem("formState", formStateString);
  };

  // Clear the storage of items if the user clicks on cancel
  const clearData = () => {
    localStorage.removeItem("checkInFormData");
    localStorage.removeItem("formState");
    localStorage.removeItem("truckForm");
    localStorage.removeItem("trailerForm");
  };

  const isLoadedOptions = useMemo(() => [true, false], []);

  return (
    <>
      {maintenanceReport && (
        <MaintenanceReportLite
          report={maintenanceReport}
          close={() => setMaintenanceReport(null)}
        />
      )}
      <div className="white-cont margin-center">
        <h2 className="global-h2 light-black">
          {t("forms.type.vehicle.check-in.header")}
        </h2>

        <form
          onSubmit={(e) => {
            nextForm(e);
            storeData();
          }}
        >
          {/* Form group: Is truck hauling trailer */}
          <div className="form-group">
            <label className="input-label required">
              {t("forms.type.vehicle.input-headers.carrying-bool")}{" "}
            </label>
            <div className="input-cont">
              <fieldset className="fieldsets">
                <div className="radio-options">
                  <input
                    type="radio"
                    name="group1"
                    data-testid="carrying-yes"
                    className="radio-input"
                    checked={formState.isCarrying}
                    onChange={() =>
                      dispatchForm({
                        type: "IS_CARRYING",
                        payload: true,
                      })
                    }
                    required
                  />
                  <label>{t("general.yes")}</label>
                </div>
                <div className="radio-options">
                  {/* If selected, reset trailer data */}
                  <input
                    type="radio"
                    name="group1"
                    data-testid="carrying-no"
                    className="radio-input"
                    checked={formState.isCarrying === false}
                    onChange={() => {
                      dispatchForm({ type: "IS_CARRYING", payload: false });
                      dispatchForm({ type: "IS_TRAILER_ATI", payload: null });
                      setFormData((prev) => ({
                        ...prev,
                        trailerNumber: null,
                        trailerPlate: null,
                        trailerCondition: null,
                        trailerDimension: null,
                      }));
                    }}
                  />
                  <label>No</label>
                </div>
              </fieldset>
            </div>
          </div>

          {/* Form group: Is this trailer ATI */}
          {formState.isCarrying && (
            <div className="form-group">
              <label className="input-label required">
                {t("forms.type.vehicle.input-headers.ati-bool")}{" "}
              </label>
              <div className="input-cont">
                <fieldset className="fieldsets">
                  <div className="radio-options">
                    <input
                      type="radio"
                      name="group2"
                      className="radio-input"
                      data-testid="ati-yes"
                      checked={formState.isTrailerAti}
                      onChange={() => {
                        dispatchForm({ type: "IS_TRAILER_ATI", payload: true });
                        resetTrailerData();
                      }}
                      required
                    />
                    <label>{t("general.yes")}</label>
                  </div>
                  <div className="radio-options">
                    <input
                      type="radio"
                      id="no1"
                      name="group2"
                      data-testid="ati-no"
                      className="radio-input"
                      checked={formState.isTrailerAti === false}
                      onChange={() => {
                        dispatchForm({
                          type: "IS_TRAILER_ATI",
                          payload: false,
                        });
                        resetTrailerData();
                      }}
                    />
                    <label>No</label>
                  </div>
                </fieldset>
              </div>
            </div>
          )}

          {/* Form group: Select carrier  */}
          <div className="form-check-in">
            <FormSelect
              options={CARRIER_OPTIONS}
              label={"carrierName"}
              value={formState.selectedCarrier}
              onChange={(e) => {
                dispatchForm({
                  type: "SELECT_CARRIER",
                  payload: e.target.value,
                });
                if (e.target.value !== "other") {
                  setFormData((prev) => ({
                    ...prev,
                    carrierName: e.target.value,
                    truckPlate: "",
                  }));
                } else {
                  setFormData((prev) => ({
                    ...prev,
                    carrierName: null,
                    truckAsset: null,
                    truckNumber: null,
                  }));
                }
              }}
            />

            {formState.selectedCarrier === "other" && (
              <FormInput
                label={"carrierName"}
                value={formData.carrierName}
                onChange={handleFormField}
              />
            )}

            {/* Form group: Truck number */}
            <div className="form-group">
              <label className="input-label required">
                {t("forms.type.vehicle.input-headers.truck-number")}{" "}
              </label>
              {formState.selectedCarrier && (
                <>
                  {formState.selectedCarrier !== "other" ? (
                    <div className="input-cont-block input-cont-message">
                      {/* Dropdown for truck list based on carrier */}
                      <div className="input-cont-margin-bottom">
                        <TruckSearchSelect
                          key={formState.selectedCarrier || "no-carrier"}
                          onSelect={handleTruckSelect}
                          carrier={formState.selectedCarrier}
                          selectedTruck={formData.truckOption}
                        />
                      </div>
                    </div>
                  ) : (
                    <div className="input-cont">
                      <input
                        className="text-input"
                        type="text"
                        id="truck-num"
                        name="truckNumber"
                        onChange={(e) => setFormData((prev) => ({
                          ...prev,
                          truckNumber: e.target.value.toUpperCase().split(" ").join(""),
                        }))}
                        value={formData.truckNumber}
                        required
                      />
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Form group: Truck license plate */}
          <FormInput
            label={"truckPlate"}
            value={formData.truckPlate}
            onChange={(e) => setFormData((prev) => ({
              ...prev,
              truckPlate: e.target.value.toUpperCase().split(" ").join(""),
            }))}
          />

          {/* Form group: Odometer */}
          <FormInput
            label={"odometer"}
            value={formData.odometer}
            type="number"
            onChange={handleFormField}
          />

          {/* Form group: Truck condition */}
          <FormSelect
            options={GUARD_VEHICLE_CONDITIONS}
            label={"truckCondition"}
            value={formData.truckCondition}
            onChange={handleConditionChange}
            disabled={formData.truckOption?.truckRepairs?.length > 0}
          />

          {formData.truckOption?.truckRepairs?.length > 0 && (
            <div className="form-group">
              <label></label>
              <div className="input-cont">
                <button
                  className="all-btn error-btn"
                  type="button"
                  onClick={() =>
                    setMaintenanceReport(formData.truckOption.truckRepairs[0])
                  }
                >
                  {t("buttons.preview")}
                </button>
              </div>
            </div>
          )}

          {/* Form group: Truck condition notes */}
          <FormInput
            label={"truckConditionNotes"}
            onChange={handleFormField}
            value={formData.truckConditionNotes}
            required={
              formData.truckCondition !== "good" &&
              formData.truckCondition !== "okay" &&
              formData.truckCondition !== "ready"
            }
          />

          {/* Form group: Driver */}
          <div className="form-group">
            <label className="input-label required">
              {t("yard-tables.search-filters.dropdowns.driver")}{" "}
            </label>
            <div className="input-cont-search">
              <DriverSearch setState={setFormData} state={formData} />
            </div>
          </div>

          {/* All trailer inputs */}
          {formState.isCarrying && formState.isTrailerAti !== null && (
            <>
              {/* // Form group: Trailer number */}
              <div className="form-group">
                <label className="input-label required">
                  {t("forms.type.vehicle.input-headers.trailer-number")}{" "}
                </label>
                {formState.isTrailerAti ? (
                  <div className="input-cont-block input-cont-message">
                    <div className="input-cont input-cont-margin-bottom">
                      <TrailerSearchSelect
                        onSelect={handleTrailerSelect}
                        selectedTrailer={formData.trailerOption}
                      />
                    </div>
                    {formState.trailerErrorMessage && (
                      <p className="error">{formState.trailerErrorMessage}</p>
                    )}
                  </div>
                ) : (
                  <div className="input-cont">
                    <input
                      type="text"
                      placeholder={t("forms.type.vehicle.placeholders.num")}
                      name="trailerNumber"
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          trailerNumber: e.target.value
                            .toUpperCase()
                            .split(" ")
                            .join(""),
                        }))
                      }
                      value={formData.trailerNumber}
                      className="text-input"
                      required
                    />
                  </div>
                )}
              </div>

              {/* Form group: Trailer license plate */}
              <FormInput
                label={"trailerPlate"}
                value={formData.trailerPlate}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    trailerPlate: e.target.value
                      .toUpperCase()
                      .split(" ")
                      .join(""),
                  }))
                }
              />

              {/* Form group: Trailer dimensions */}
              <div className="form-group">
                <label className="input-label required">
                  {t("forms.type.vehicle.input-headers.trailer-dim")}
                </label>
                <div className="input-cont">
                  <fieldset className="fieldsets">
                    <div className="radio-options">
                      <input
                        id="dimension1"
                        data-testid="dimension1"
                        type="radio"
                        name="dimension"
                        className="radio-input"
                        checked={formState.trailerDimension === 1}
                        onChange={(e) => {
                          dispatchForm({
                            type: "SET_TRAILER_DIMENSION",
                            payload: 1,
                          });
                          handleTrailerDimensions(e);
                        }}
                        required
                      />
                      <label>53'</label>
                    </div>
                    <div className="radio-options">
                      <input
                        id="dimension2"
                        data-testid="dimension2"
                        type="radio"
                        name="dimension"
                        className="radio-input"
                        checked={formState.trailerDimension === 2}
                        onChange={(e) => {
                          dispatchForm({
                            type: "SET_TRAILER_DIMENSION",
                            payload: 2,
                          });
                          handleTrailerDimensions(e);
                        }}
                      />
                      <label>48'</label>
                    </div>
                    <div className="radio-options">
                      <input
                        id="dimension3"
                        data-testid="dimension3"
                        type="radio"
                        name="dimension"
                        className="radio-input"
                        checked={formState.trailerDimension === 3}
                        onChange={(e) => {
                          dispatchForm({
                            type: "SET_TRAILER_DIMENSION",
                            payload: 3,
                          });
                          handleTrailerDimensions(e);
                        }}
                      />
                      <label>40'</label>
                    </div>
                    <div className="radio-options">
                      <input
                        id="dimension4"
                        data-testid="dimension4"
                        type="radio"
                        name="dimension"
                        className="radio-input"
                        checked={formState.trailerDimension === 4}
                        onChange={(e) => {
                          dispatchForm({
                            type: "SET_TRAILER_DIMENSION",
                            payload: 4,
                          });
                          handleTrailerDimensions(e);
                        }}
                      />
                      <label>
                        {t("forms.type.vehicle.placeholders.other")}
                      </label>
                    </div>
                  </fieldset>
                </div>
              </div>

              {/* Form group: Custom dimensions */}
              {formState.trailerDimension === 4 && (
                <div className="form-group">
                  <label className="input-label required">
                    {t("forms.type.vehicle.input-headers.other-dimensions")}
                  </label>
                  <div className="input-cont">
                    <input
                      type="number"
                      step="0.01"
                      inputMode="numeric"
                      name="trailerLength"
                      data-testid="trailerLength"
                      min={0}
                      className="text-input"
                      value={formData.trailerLength}
                      onChange={handleFormField}
                      placeholder={t("forms.type.vehicle.placeholders.length")}
                      required
                    />
                    <input
                      type="number"
                      step="0.01"
                      inputMode="numeric"
                      name="trailerWidth"
                      data-testid="trailerWidth"
                      min={0}
                      className="text-input"
                      value={formData.trailerWidth}
                      onChange={handleFormField}
                      placeholder={t("forms.type.vehicle.placeholders.width")}
                      required
                    />
                    <input
                      type="number"
                      inputMode="numeric"
                      name="trailerHeight"
                      data-testid="trailerHeight"
                      step="0.01"
                      min={0}
                      className="text-input"
                      value={formData.trailerHeight}
                      onChange={handleFormField}
                      placeholder={t("forms.type.vehicle.placeholders.height")}
                      required
                    />
                  </div>
                </div>
              )}

              {/* Form group: Is loaded */}
              <FormSelect
                options={isLoadedOptions}
                label={"isLoaded"}
                value={formData.isLoaded}
                onChange={handleConditionChange}
              />
              {/* Form group: Customer */}
              <div className="form-group">
                <label className="input-label required">
                  {t("yard-tables.search-filters.dropdowns.customer")}
                </label>
                <div className="input-cont">
                  <div className="search-input">
                    <input
                      id="searchCustomer"
                      type="text"
                      className="text-input"
                      name="customer"
                      placeholder="'N/A' si es desconocido"
                      value={formData.customer}
                      data-testid="customer"
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                </div>
              </div>

              {/* Form group: Seal */}
              <div>
                <FormInput
                  label={"sealNumber"}
                  value={formData.sealNumber}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      sealNumber: e.target.value
                        .toUpperCase()
                        .split(" ")
                        .join(""),
                    }))
                  }
                  required={formData.isLoaded === "true" ? true : false}
                />
              </div>

              {/* Form group: Internal seal */}
              <div className="form-group">
                <label className="input-label">
                  {t("forms.type.vehicle.input-headers.inventorySeal")}
                </label>
                <div className="input-cont">
                  <SealInventoryInput
                    onSelect={(option) =>
                      setFormData((prev) => ({
                        ...prev,
                        internalSeal: option ? option : null,
                      }))
                    }
                    selectedOption={formData.internalSeal}
                  />
                </div>
              </div>

              {/* Driver license only if internal seal is populated */}
              {formData.internalSeal && (
                <div>
                  <FormInput
                    label={"driverLicense"}
                    value={formData.driverLicense}
                    onChange={handleFormField}
                  />
                </div>
              )}

              {/* Form group: Trailer condition */}
              <FormSelect
                label={"trailerCondition"}
                options={GUARD_VEHICLE_CONDITIONS}
                value={formData.trailerCondition}
                onChange={handleConditionChange}
                disabled={formData.trailerOption?.trailerRepairs?.length > 0}
              />

              {formData.trailerOption?.trailerRepairs?.length > 0 && (
                <div className="form-group">
                  <label></label>
                  <div className="input-cont">
                    <button
                      className="all-btn error-btn"
                      type="button"
                      onClick={() =>
                        setMaintenanceReport(
                          formData.trailerOption.trailerRepairs[0],
                        )
                      }
                    >
                      {t("buttons.preview")}
                    </button>
                  </div>
                </div>
              )}

              {/* Form group: Trailer condition notes */}
              <FormInput
                label={"trailerConditionNotes"}
                value={formData.trailerConditionNotes}
                onChange={handleFormField}
                required={
                  formData.trailerCondition !== "good" &&
                  formData.trailerCondition !== "okay" &&
                  formData.trailerCondition !== "ready"
                }
              />
            </>
          )}

          <div className="form-group">
            <label className="input-label">
              {t("forms.type.vehicle.input-headers.origin")}
            </label>
          </div>
          {/* Form group: Origin */}
          <FormSelect
            options={COUNTRY_OPTIONS}
            label={"country"}
            value={formData.country}
            onChange={handleConditionChange}
          />

          {/* Form group: State */}
          <div className="form-group">
            <label className="input-label required">
              {t("forms.type.vehicle.labels.state")}
            </label>
            <div className="input-cont">
              <select
                id="state"
                name="state"
                data-testid="state"
                value={formData.state}
                onChange={handleConditionChange}
                required
              >
                <option value="" selected disabled>
                  {t("forms.type.vehicle.placeholders.destination.state")}
                </option>
                {formData.country === "USA"
                  ? usStates.map((state, index) => (
                      <option key={index} value={state}>
                        {state}
                      </option>
                    ))
                  : mexicanStates.map((state, index) => (
                      <option key={index} value={state}>
                        {state}
                      </option>
                    ))}
              </select>
            </div>
          </div>

          {/* Form group: City */}
          <FormInput
            label={"city"}
            value={formData.city}
            onChange={handleFormField}
          />

          {/* Form group: Notes */}
          <FormInput
            label={"notes"}
            value={formData.notes}
            onChange={handleFormField}
            required={false}
          />

          {/* Buttons cont */}
          <div className="full-cont">
            <button
              className="change-pwd-btn"
              id="continue-btn"
              data-testid="continue-btn"
              type="submit"
            >
              {t("buttons.continue")}
            </button>
            <button
              className="cancel-btn"
              type="button"
              onClick={() => {
                close();
                clearData();
              }}
            >
              {t("buttons.cancel")}
            </button>
          </div>
        </form>
      </div>
    </>
  );
};

export default React.memo(CheckInVehicleForm);
